import { DeleteOutlined } from '@ant-design/icons';
import { Button, Modal, notification, Table, Tooltip, Typography } from 'antd';
import type { Breakpoint } from 'antd/es/_util/responsiveObserver';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { BankResponse } from '../../model/Bank.model';
import bankService from '../../service/bank-service/Bank.service';
import EditBankModal from './EditBankModal';

interface BankListTableProps {
  bankList: BankResponse[];
  onSuccess: () => void;
}

const BankListTable: React.FC<BankListTableProps> = ({
  bankList,
  onSuccess,
}) => {
  const [bankToDelete, setBankToDelete] = useState<BankResponse | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(9);

  const handleDelete = async () => {
    if (bankToDelete) {
      try {
        await bankService.deleteBank(bankToDelete.id);
        onSuccess();
        notification.success({ message: 'Xóa ngân hàng thành công' });
        setBankToDelete(null);
      } catch (error) {
        console.error('Error deleting bank:', error);
        notification.error({ message: 'Xóa ngân hàng thất bại' });
      }
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const columns: ColumnsType<BankResponse> = [
    {
      title: 'STT',
      key: 'index',
      width: 70,
      render: (_: any, __: any, index: number) =>
        (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: 'Tên ngân hàng',
      dataIndex: 'name',
      key: 'name',
      responsive: ['md' as Breakpoint],
    },
    {
      title: 'Tạo khi',
      dataIndex: 'createdAt',
      key: 'createdAt',
      responsive: ['xl' as Breakpoint],
      render: (date: string) => (date ? dayjs(date).format('DD/MM/YYYY') : ''),
    },
    {
      title: 'Cập nhật khi',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      responsive: ['xl' as Breakpoint],
      render: (date: string) => (date ? dayjs(date).format('DD/MM/YYYY') : ''),
    },
    {
      title: 'Hành động',
      key: 'action',
      render: (_: any, record: BankResponse) => (
        <div className="flex items-center gap-2">
          <EditBankModal bank={record} onSuccess={onSuccess} />
          <Tooltip title="Xóa">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => setBankToDelete(record)}
              className="flex items-center justify-center"
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <Table
        columns={columns}
        dataSource={bankList}
        rowKey="id"
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: bankList.length,
          onChange: handlePageChange,
          showSizeChanger: false,
          className: 'mt-4',
          position: ['bottomRight'],
        }}
        className="w-full border border-gray-100 shadow-md rounded-lg overflow-hidden"
        rowClassName="odd:bg-white even:bg-gray-50 hover:bg-gray-100"
        size="middle"
        scroll={{ x: 'max-content' }}
      />

      <Modal
        title="Xác nhận xóa"
        open={bankToDelete !== null}
        onCancel={() => setBankToDelete(null)}
        footer={[
          <Button key="cancel" onClick={() => setBankToDelete(null)}>
            Hủy
          </Button>,
          <Button key="delete" type="primary" danger onClick={handleDelete}>
            Xóa
          </Button>,
        ]}
      >
        <p>Bạn có chắc muốn xóa ngân hàng này?</p>
      </Modal>
    </div>
  );
};

export default BankListTable;
